import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { pptToVideo, analyzePpt, checkTaskStatus } from '../../services/videoApi';
import { 
    Upload, Download, Loader2, AlertCircle, 
    CheckCircle, XCircle, FileUp, Mic
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';


const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};



const VOICE_OPTIONS = {
    "alloy": "Alloy (Neutral)",
    "echo": "Echo (Male)",
    "fable": "Fable (British Male)",
    "onyx": "Onyx (Deep Male)",
    "nova": "Nova (Female)",
    "shimmer": "Shimmer (Female)"
};

const PptToVideo = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    
    // UI State
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [slideCount, setSlideCount] = useState(0);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    
    // Process Inputs
    const [voice, setVoice] = useState('alloy');
    const [silentSlides, setSilentSlides] = useState([]); // Array of numbers
    
    // Execution State
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [progressStep, setProgressStep] = useState('');
    const [progressValue, setProgressValue] = useState(0);
    const [resultUrl, setResultUrl] = useState(null);
    
    const pollingIntervalRef = useRef(null);

    // Polling Logic
    useEffect(() => {
        if (taskId && isProcessing) {
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const data = await checkTaskStatus(taskId);
                    
                    if (data.state === 'SUCCESS' || data.status === 'SUCCESS') {
                        clearInterval(pollingIntervalRef.current);
                        const payload = data.result;
                        if (payload.new_credits !== undefined) setCredits(payload.new_credits);
                        if (payload.result && payload.result.download_url) {
                            setResultUrl(payload.result.download_url);
                            setProgressValue(100);
                            setIsProcessing(false);
                        }
                    } else if (data.state === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(data.error);
                        setIsProcessing(false);
                    } else {
                        if (data.info) {
                            if (data.info.step) setProgressStep(data.info.step);
                            if (data.info.progress) setProgressValue(data.info.progress);
                        }
                    }
                } catch (err) {
                    clearInterval(pollingIntervalRef.current);
                    setError(err.message);
                    setIsProcessing(false);
                }
            }, 3000);
        }
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [taskId, isProcessing, setCredits]);

    const handleFileChange = async (e) => {
        const selected = e.target.files[0];
        if (selected) {
            // Basic validation
            if (!selected.name.endsWith('.pptx')) {
                setError(t('pptToVideo.errors.invalidFormat'));
                return;
            }
            if (selected.size > 50 * 1024 * 1024) { // 50MB limit
                setError(t('pptToVideo.errors.tooLarge'));
                return;
            }

            setFile(selected);
            setFileName(selected.name);
            setError(null);
            setResultUrl(null);
            setSilentSlides([]);
            
            // Analyze File immediately to get slide count
            setIsAnalyzing(true);
            try {
                const data = await analyzePpt(selected);
                setSlideCount(data.slide_count);
            } catch (err) {
                setError("Could not analyze PowerPoint file. Please check if it's valid.");
                setFile(null);
                setFileName('');
            } finally {
                setIsAnalyzing(false);
            }
        }
    };

    const toggleSlideSelection = (num) => {
        setSilentSlides(prev => 
            prev.includes(num) ? prev.filter(n => n !== num) : [...prev, num]
        );
    };

    const handleProcess = async () => {
        if (!file) return;
        setIsProcessing(true);
        setProgressValue(0);
        setError(null);
        
        try {
            const response = await pptToVideo(file, voice, silentSlides);
            setTaskId(response.task_id);
        } catch (err) {
            setError(err.message);
            setIsProcessing(false);
        }
    };

    const handleClear = () => {
        setFile(null);
        setFileName('');
        setSlideCount(0);
        setSilentSlides([]);
        setResultUrl(null);
        setError(null);
        const el = document.getElementById('ppt-upload');
        if (el) el.value = '';
    };
    const getProgressLabel = (step) => {
        if (!step) return t('pptToVideo.processing');

        // Check if the step string looks like "processing_slide_X"
        if (step.startsWith('processing_slide_')) {
            // Extract the number after the last underscore
            const slideNum = step.split('_').pop(); 
            
            // Use the interpolation key we added to JSON
            return t('pptToVideo.steps.processing_slide', { number: slideNum });
        }

        // Handle standard static steps (downloading, rendering, etc.)
        // Uses 'processing' as a fallback if the step key isn't found
        return t(`pptToVideo.steps.${step}`, { defaultValue: t('pptToVideo.processing') });
    };

    const UploadView = () => (
        <div className="max-w-4xl mx-auto">
            {/* --- NEW HEADER SECTION START --- */}
            <h3 className="text-2xl font-bold mb-4 text-brand-orange">
                {t('pptToVideo.title')}
            </h3>
            
            
                <p className="mb-4 text-brand-dark leading-relaxed">
                    {t('pptToVideo.intro')}
                </p>
                
                <p className="font-bold mb-2 text-brand-dark">
                    {t('pptToVideo.notesHeader')}
                </p>
                <ul className="list-disc list-inside space-y-1 text-brand-dark ml-2">
                    <li>{t('pptToVideo.notes.one')}</li>
                    <li>{t('pptToVideo.notes.two')}</li>
                    <li>{t('pptToVideo.notes.three')}</li>
                </ul>
            
            {/* --- NEW HEADER SECTION END --- */}
            
            <div className="mt-6 space-y-6">
                {/* Upload Area */}
                <div>
                    <label className="block text-sm font-medium mb-2 text-brand-dark">{t('pptToVideo.uploadLabel')}</label>
                    <div className="flex items-center p-4 border-2 border-dashed border-slate-400 rounded-md bg-slate-50">
                        <FileUp className="h-8 w-8 text-slate-500" />
                        <label htmlFor="ppt-upload" className="ml-4 text-sm text-brand-orange font-semibold cursor-pointer hover:text-orange-600">
                            {t('pptToVideo.selectFile')}
                            <input 
                                id="ppt-upload" 
                                type="file" 
                                className="sr-only" 
                                onChange={handleFileChange} 
                                accept=".pptx" 
                            />
                        </label>
                        {fileName && (
                            <div className="ml-auto flex items-center gap-2 text-sm text-slate-700">
                                <span className="truncate max-w-[200px]">{fileName}</span>
                                <button onClick={handleClear}><XCircle className="h-5 w-5 text-red-500" /></button>
                            </div>
                        )}
                    </div>
                </div>

                {isAnalyzing && (
                    <div className="text-sm text-slate-500 flex items-center">
                        <Loader2 className="animate-spin mr-2 h-4 w-4" /> Analyzing presentation...
                    </div>
                )}

                {/* Configuration (Only show if file analyzed successfully) */}
                {slideCount > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-4">
                        {/* Voice Selection */}
                        <div>
                            <label className="block text-sm font-medium mb-2 text-brand-dark">
                                <Mic className="inline w-4 h-4 mr-1"/> {t('pptToVideo.voiceLabel')}
                            </label>
                            <select 
                                value={voice}
                                onChange={(e) => setVoice(e.target.value)}
                                className="w-full p-2 border border-slate-300 rounded-md"
                            >
                                {Object.entries(VOICE_OPTIONS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Silent Slides Selector */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium mb-2 text-brand-dark">
                                {t('pptToVideo.silentSlidesLabel')}
                            </label>
                            <p className="text-xs text-slate-500 mb-2">{t('pptToVideo.silentSlidesHelp')}</p>
                            
                            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border border-slate-200 rounded-md">
                                {Array.from({length: slideCount}, (_, i) => i + 1).map(num => (
                                    <button
                                        key={num}
                                        onClick={() => toggleSlideSelection(num)}
                                        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                                            silentSlides.includes(num)
                                                ? 'bg-slate-700 text-white hover:bg-slate-800'
                                                : 'bg-white border border-slate-300 text-slate-700 hover:border-brand-orange'
                                        }`}
                                    >
                                        {num}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {error && (
                    <p className="mt-2 text-red-500 flex items-center"><AlertCircle size={16} className="mr-2"/>{error}</p>
                )}

                <button
                    onClick={handleProcess}
                    disabled={!file || isAnalyzing || isProcessing || slideCount === 0}
                    className="w-full md:w-auto px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center"
                >
                    {isProcessing ? (
                        <><Loader2 className="animate-spin mr-2" size={20} /> {t('pptToVideo.processing')}</>
                    ) : (
                        t('pptToVideo.processButton')
                    )}
                </button>

                {isProcessing && (
                    <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
                        <div className="flex justify-between text-sm font-medium text-slate-700 mb-2">
                            <span>{getProgressLabel(progressStep)}</span>
                            <span>{progressValue}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                            <div className="bg-brand-orange h-2.5 rounded-full transition-all duration-500" style={{ width: `${progressValue}%` }}></div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const ResultView = () => (
        <div className="max-w-4xl mx-auto text-center">
            <div className="bg-green-50 border border-green-200 p-4 rounded-md mb-8 flex items-center justify-center text-green-800">
                <CheckCircle className="mr-2" /> {t('pptToVideo.success')}
            </div>

            <div className="rounded-lg overflow-hidden bg-black aspect-video mb-6 shadow-lg">
                <video src={resultUrl} controls className="w-full h-full object-contain" />
            </div>

            <a 
                href={resultUrl} 
                download={`presentation_video_${Date.now()}.mp4`}
                className="inline-flex items-center px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                <Download className="mr-2" size={20} />
                {t('pptToVideo.download')}
            </a>
            
            <button onClick={handleClear} className="block w-full mt-6 text-slate-600 hover:text-brand-orange underline">
                {t('pptToVideo.processAnother')}
            </button>
        </div>
    );

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt /> 
            ) : user?.credits < 0 ? (
                <PurchaseCreditsPrompt />
            ) : resultUrl ? (
                <ResultView />
            ) : (
                <UploadView />
            )}
        </div>
    );
};

export default PptToVideo;
